import React, { useState, useEffect } from "react";
import contact from "../../asset/Rectangle 4172.png";
import { sendAdminEmail, sendUserEmail } from "../EmailJs/emailService";
import toast, { Toaster } from "react-hot-toast";
import ReactGA from 'react-ga4'
const CustomCaptcha = ({ onCaptchaChange, resetTrigger }) => {
  const [captchaText, setCaptchaText] = useState("");
  const [userInput, setUserInput] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [charOffsets, setCharOffsets] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const generateCaptcha = () => {
    // Stop any ongoing speech when generating new CAPTCHA
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }

    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    let offsets = [];
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
      offsets.push((Math.random() * 10 - 5).toFixed(2));
    }
    setCaptchaText(result);
    setCharOffsets(offsets);
    setUserInput("");
    setIsValid(false);
    onCaptchaChange && onCaptchaChange(false);
  };

  // Generate CAPTCHA immediately when component mounts
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Reset captcha when resetTrigger changes
  useEffect(() => {
    if (resetTrigger) {
      generateCaptcha();
    }
  }, [resetTrigger]);

  useEffect(() => {
    const timer = setInterval(() => {
      generateCaptcha();
    }, 60000);

    return () => {
      clearInterval(timer);
      // Stop any ongoing speech when component unmounts
      if (isSpeaking) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isSpeaking]);

  const speakCaptcha = () => {
    if ("speechSynthesis" in window) {
      // Stop any ongoing speech before starting new one
      window.speechSynthesis.cancel();
      setIsSpeaking(true);

      const voices = window.speechSynthesis.getVoices();
      const maleUsVoice =
        voices.find(
          (voice) =>
            voice.lang === "en-US" && voice.name.toLowerCase().includes("david")
        ) || voices.find((voice) => voice.lang === "en-US");

      let currentIndex = 0;
      const speakNextChar = () => {
        if (currentIndex < captchaText.length) {
          const char = captchaText[currentIndex];
          const utterance = new SpeechSynthesisUtterance(char);
          utterance.rate = 0.5;
          utterance.pitch = 0.9;
          utterance.volume = 1.0;
          utterance.lang = "en-US";

          if (maleUsVoice) {
            utterance.voice = maleUsVoice;
          }

          utterance.onend = () => {
            currentIndex++;
            speakNextChar();
          };

          window.speechSynthesis.speak(utterance);
        } else {
          setIsSpeaking(false);
        }
      };

      speakNextChar();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setUserInput(value);
    const valid = value === captchaText;
    setIsValid(valid);
    onCaptchaChange && onCaptchaChange(valid);
  };

  const handleAudioToggle = (e) => {
    setAudioEnabled(e.target.checked);
  };

  return (
    <div className="mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="bg-gray-100 p-3 rounded font-mono text-lg tracking-wider select-none relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `repeating-linear-gradient(
                0deg,
                #ccc,
                #ccc 1px,
                transparent 1px,
                transparent 5px
              )`,
              backgroundSize: "100% 10px",
              backgroundPosition: "0 50%",
            }}
          />
          <div className="relative z-10">
            {captchaText.split("").map((char, index) => (
              <span
                key={index}
                style={{
                  transform: `translateY(${charOffsets[index]}px)`,
                  display: "inline-block",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                }}
                className="mx-0.5"
              >
                {char}
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-2 items-center justify-left sm:justify-start">
          <button
            type="button"
            onClick={generateCaptcha}
            className="px-3 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
            title="Refresh CAPTCHA"
          >
            ↻
          </button>
          {audioEnabled && (
            <button
              type="button"
              onClick={speakCaptcha}
              disabled={isSpeaking}
              className={`px-3 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 ${isSpeaking ? "opacity-50 cursor-not-allowed" : ""
                }`}
              title="Listen to CAPTCHA"
            >
              {isSpeaking ? "🔊🎵" : "🔊"}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center mt-2">
        <input
          type="checkbox"
          id="enableAudio"
          checked={audioEnabled}
          onChange={handleAudioToggle}
          className="mr-2"
        />
        <label htmlFor="enableAudio" className="text-sm text-gray-700">
          Enable Audio
        </label>
      </div>

      <div className="mt-3">
        <input
          type="text"
          value={userInput}
          onChange={handleInputChange}
          placeholder="Enter CAPTCHA"
          className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${userInput !== "" && !isValid
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300"
            }`}
        />
        {userInput !== "" && !isValid && (
          <p className="text-red-500 text-sm mt-1">CAPTCHA does not match</p>
        )}
        {isValid && (
          <p className="text-green-500 text-sm mt-1">
            CAPTCHA verified successfully
          </p>
        )}
      </div>
    </div>
  );
};

// Function to get the initial landing URL
let initialLandingUrl = null;

const getSourceUrl = () => {
  if (typeof window === "undefined") return "Unknown";

  // If we haven't stored the initial URL yet, store it
  if (!initialLandingUrl) {
    initialLandingUrl = window.location.href;
  }

  return initialLandingUrl;
};

// Function to get IP address
const getIPAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Failed to get IP address:", error);
    return "IP address not available";
  }
};

// Function to get Form Details
const getFormDetails = () => {
  const path = window.location.pathname;

  if (path === "/" || path === "") {
    return { formname: "Home Form" };
  }

  const pageKey = path.replace("/", "");

  const readable = pageKey
    .replace(/Main$/i, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

  return { formname: `${readable} Form` };
};

// Enhanced phone validation function
const validatePhone = (phone) => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Check if it's exactly 10 digits
  if (cleaned.length !== 10) {
    return false;
  }

  // Check if area code is valid (doesn't start with 0 or 1)
  const areaCode = cleaned.substring(0, 3);
  if (areaCode[0] === '0' || areaCode[0] === '1') {
    return false;
  }

  // Check if exchange code is valid (doesn't start with 0 or 1)
  const exchangeCode = cleaned.substring(3, 6);
  if (exchangeCode[0] === '0' || exchangeCode[0] === '1') {
    return false;
  }

  return true;
};

export default function ContactUsPage() {
  const [pingUrl, setPingUrl] = useState("");
  const [certId, setCertId] = useState("");
  const [tokenUrl, setTokenUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaResetTrigger, setCaptchaResetTrigger] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);

  const usStates = [
    { value: "", label: "Select a state" },
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
    { value: "DC", label: "District of Columbia" },
    { value: "AS", label: "American Samoa" },
    { value: "GU", label: "Guam" },
    { value: "MP", label: "Northern Mariana Islands" },
    { value: "PR", label: "Puerto Rico" },
    { value: "VI", label: "U.S. Virgin Islands" },
  ];

  useEffect(() => {
    let observer;
    let timeoutId;

    const initializeTrustedForm = () => {
      // Check for initial values
      const checkInitialValues = () => {
        const certUrlField = document.querySelector(
          'input[name="xxTrustedFormCertUrl"]'
        );
        const tokenField = document.querySelector(
          'input[name="xxTrustedFormCertToken"]'
        );
        const pingUrlField = document.querySelector(
          'input[name="xxTrustedFormPingUrl"]'
        );

        if (certUrlField?.value) {
          setCertId(certUrlField.value);
        }
        if (tokenField?.value) {
          setTokenUrl(tokenField.value);
        }
        if (pingUrlField?.value) {
          setPingUrl(pingUrlField.value);
        }
      };

      // Set up mutation observer
      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "value"
          ) {
            const fieldName = mutation.target.name;

            // Extract values from all TrustedForm fields
            const certUrlField = document.querySelector(
              'input[name="xxTrustedFormCertUrl"]'
            );
            const tokenField = document.querySelector(
              'input[name="xxTrustedFormCertToken"]'
            );
            const pingUrlField = document.querySelector(
              'input[name="xxTrustedFormPingUrl"]'
            );

            const certUrlValue = certUrlField?.value || "";
            const tokenValue = tokenField?.value || "";
            const pingUrlValue = pingUrlField?.value || "";

            // Update state with the correct values
            setCertId(certUrlValue);
            setPingUrl(pingUrlValue);
            setTokenUrl(tokenValue);

            // Fetch cert data if we have a cert URL and it's the cert field that changed
            if (certUrlValue && fieldName === "xxTrustedFormCertUrl") {
              fetchCertData(certUrlValue);
            }
          }
        });
      });

      // Observe all TrustedForm fields for changes
      const fieldsToObserve = [
        "xxTrustedFormCertUrl",
        "xxTrustedFormCertToken",
        "xxTrustedFormPingUrl",
      ];

      fieldsToObserve.forEach((fieldName) => {
        const field = document.querySelector(`input[name="${fieldName}"]`);
        if (field) {
          observer.observe(field, { attributes: true });
        }
      });

      // Check for initial values after a delay to allow TrustedForm to load
      timeoutId = setTimeout(checkInitialValues, 1000);
    };

    // Initialize TrustedForm monitoring
    initializeTrustedForm();

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const fetchCertData = async (certUrl) => {
    try {
      const response = await fetch(certUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("TrustedForm Cert Data:", data);
      return data;
    } catch (error) {
      console.error("Error fetching TrustedForm cert:", error);
      return null;
    }
  };

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    state: "",
    additionalInfo: "",
    agreeToPrivacy: false,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Enhanced phone input handler with real-time formatting
  const handlePhoneChange = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = cleaned.slice(0, 10);

    // Format based on length
    let formattedValue = limited;
    if (limited.length >= 6) {
      formattedValue = `(${limited.substring(0, 3)}) ${limited.substring(3, 6)}-${limited.substring(6)}`;
    } else if (limited.length >= 3) {
      formattedValue = `(${limited.substring(0, 3)}) ${limited.substring(3)}`;
    } else if (limited.length > 0) {
      formattedValue = `(${limited}`;
    }

    handleInputChange("phone", formattedValue);
  };

  const handleCaptchaChange = (isValid) => {
    setCaptchaValid(isValid);
  };

  const handleHumanVerificationChange = (checked) => {
    setShowCaptcha(checked);
    if (!checked) {
      setCaptchaValid(false);
    }
  };

  // ----------------- VALIDATION -----------------
  const validateForm = () => {
    if (!formData.firstname.trim()) {
      toast.error("First Name is required");
      return false;
    }
    if (!formData.lastname.trim()) {
      toast.error("Last Name is required");
      return false;
    }
    if (!formData.state.trim()) {
      toast.error("State is required");
      return false;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Enter a valid email address");
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error("Phone number is required");
      return false;
    }

    // Enhanced phone validation
    const cleanedPhone = formData.phone.replace(/\D/g, '');
    if (cleanedPhone.length !== 10) {
      toast.error("Enter a valid 10-digit US phone number");
      return false;
    }

    // Additional US phone number validation
    const areaCode = cleanedPhone.substring(0, 3);
    if (areaCode[0] === '0' || areaCode[0] === '1') {
      toast.error("Please enter a valid US area code");
      return false;
    }

    const exchangeCode = cleanedPhone.substring(3, 6);
    if (exchangeCode[0] === '0' || exchangeCode[0] === '1') {
      toast.error("Please enter a valid US phone number");
      return false;
    }

    if (!formData.additionalInfo.trim()) {
      toast.error("Message is required");
      return false;
    }
    if (!showCaptcha) {
      toast.error("Please verify that you are human");
      return false;
    }
    if (!captchaValid) {
      toast.error("Please complete the CAPTCHA verification");
      return false;
    }
    if (!formData.agreeToPrivacy) {
      toast.error("You must agree to the Privacy Policy");
      return false;
    }
    return true;
  };

  // ----------------- HANDLE SUBMIT -----------------
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const submitData = {
      ...formData,
      certId,
      pingUrl,
      tokenUrl,
    };

    try {
      // Show loading toast
      toast.loading("Submitting form...", { id: "submit" });

      await sendAdminEmail(submitData);
      await sendUserEmail(submitData);
      await fetch(
        'https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            countryName: 'Usa',
            brandName: 'Belief Marketing Enterprise',
            websiteName: 'Belief Marketing',
            formname: getFormDetails().formname,
            data: {
              name: `${submitData.firstname} ${submitData.lastname}`.trim(),
              firstname: submitData.firstname,
              lastname: submitData.lastname,
              email: submitData.email,
              phone: submitData.phone,
              state: submitData.state,
              additionalInfo: submitData.additionalInfo,
              ipAddress: await getIPAddress(),
              trustedFormToken: submitData.certId,
              trustedFormPingUrl: submitData.pingUrl,
              trustedFormCertUrl: submitData.tokenUrl,
              submissionDate: new Date().toISOString(),
              pageSource: getSourceUrl(),
            },
          }),
        }
      );

      // Success toast
      toast.success("Form submitted successfully!", { id: "submit" });

      // Reset form
      setFormData({
        firstname: "",
        lastname: "",
        state: "",
        email: "",
        phone: "",
        additionalInfo: "",
        agreeToPrivacy: false,
      });

      // Reset captcha and human verification
      setShowCaptcha(false);
      setCaptchaResetTrigger(prev => prev + 1);
      setCaptchaValid(false);
      ReactGA.event({
      category: "Form",
      action: "Submit",
      label: "ContactUs Form"
    });
 
    } catch (error) {
      ReactGA.event({
      category: "Form",
      action: "Submission Failed",
      label: error.message
    });
 
      console.error(error);
      toast.error("Something went wrong. Please try again later.", { id: "submit" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f1e8] p-4 md:p-8">
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
          loading: {
            iconTheme: {
              primary: '#FC821F',
              secondary: '#fff',
            },
          }
        }}
      />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Left Column - Form */}
          <div className="space-y-6">
            <div className="space-y-4 relative">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A476C] leading-tight">
                Get Legal
                <br />
                Help
              </h1>
              <p className="text-[#0A476C] text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed max-w-xl mt-6">
                Our legal team will reach out to you as soon as possible with more
                information regarding your rights and options.
              </p>
              {/* Full-width orange line under the heading */}
              <div className="absolute -bottom-2 left-0 w-full h-2 bg-[#FC821F]"></div>
            </div>

            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              {/* TrustedForm creates its own hidden fields dynamically - no need for React fields */}
              <input
                type="hidden"
                id="xxTrustedFormCertUrl"
                name="xxTrustedFormCertUrl"
                value={certId}
              />
              <input
                type="hidden"
                id="xxTrustedFormCertToken"
                name="xxTrustedFormCertToken"
                value={tokenUrl}
              />
              <input
                type="hidden"
                id="xxTrustedFormPingUrl"
                name="xxTrustedFormPingUrl"
                value={pingUrl}
              />

              {/* First Name and Last Name Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#0A476C] text-sm font-medium mb-2">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstname}
                    onChange={(e) => handleInputChange("firstname", e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FC821F] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-[#0A476C] text-sm font-medium mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastname}
                    onChange={(e) => handleInputChange("lastname", e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FC821F] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Phone and Email Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#0A476C] text-sm font-medium mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="(123) 456-7890"
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FC821F] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  {formData.phone && !validatePhone(formData.phone) && (
                    <p className="text-red-500 text-xs mt-1">Please enter a valid 10-digit US phone number</p>
                  )}
                </div>
                <div>
                  <label className="block text-[#0A476C] text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FC821F] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* State Select */}
              <div>
                <label className="block text-[#0A476C] text-sm font-medium mb-2">State *</label>
                <select
                  value={formData.state}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FC821F] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {usStates.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <p className="text-[#0A476C] text-sm mb-2">Message / Inquiry *</p>
                <textarea
                  value={formData.additionalInfo}
                  onChange={(e) => handleInputChange("additionalInfo", e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm min-h-[120px] resize-none focus:outline-none focus:ring-2 focus:ring-[#FC821F] disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              {/* Privacy */}
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={formData.agreeToPrivacy}
                  onChange={(e) => handleInputChange("agreeToPrivacy", e.target.checked)}
                  disabled={isSubmitting}
                  className="mt-1 h-4 w-4 text-[#FC821F] border-gray-300 rounded disabled:cursor-not-allowed"
                />
                <label htmlFor="privacy" className="text-xs text-[#0A476C] leading-relaxed">
                  I agree to{" "}
                  <a href="#" className="text-blue-600 underline">
                    Belief Marketing Enterprise Privacy Policy .
                  </a>
                  By completing this form, I agree to Helping Survivors Privacy Policy. Helping Survivors may connect me with a law firm to discuss the merits of my potential case. I agree to be contacted via phone, email, & or text message directly by a law firm.
                </label>
              </div>

              {/* Human Verification */}
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="humanVerification"
                  checked={showCaptcha}
                  onChange={(e) => handleHumanVerificationChange(e.target.checked)}
                  disabled={isSubmitting}
                  className="mt-1 h-4 w-4 text-[#FC821F] border-gray-300 rounded disabled:cursor-not-allowed"
                />
                <label htmlFor="humanVerification" className="text-xs text-[#0A476C] leading-relaxed">
                  Verify you are human
                </label>
              </div>

              {/* CAPTCHA Section - Only show when human verification is checked */}
              {showCaptcha && (
                <div>
                  <label className="block text-[#0A476C] text-sm font-medium mb-2">Security Verification *</label>
                  <CustomCaptcha
                    onCaptchaChange={handleCaptchaChange}
                    resetTrigger={captchaResetTrigger}
                  />
                </div>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full px-8 py-4 text-lg font-semibold transition-colors duration-200 ${isSubmitting
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-[#0A476C] text-white hover:bg-[#0A476C] hover:text-white"
                  }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  "Submit"
                )}
              </button>
            </form>
          </div>

          {/* Right Column - Image & Info */}
          <div className="relative flex flex-col items-center lg:items-end space-y-6 w-full">
            {/* Spacer (optional) */}
            <div className="h-16 sm:h-24 lg:h-32"></div>

            {/* Image Container */}
            <div className="relative flex justify-center w-full">
              <div className="w-80 sm:w-96 lg:w-[28rem] h-80 sm:h-96 lg:h-[28rem] relative rounded-lg overflow-hidden">
                <img
                  src={contact}
                  alt="Contact us"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Info Text */}
            <div className="text-center lg:text-right max-w-xs sm:max-w-sm lg:max-w-xs mx-auto lg:mx-0">
              <p className="text-[#0A476C] font-medium text-sm sm:text-base leading-relaxed italic">
                We're here to help survivors of sexual assault, harassment, and abuse
                understand their rights and options.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}